import { t } from "@/i18n-js/instance";
import { AccordionV2 } from "@/react/components/shared/uikit/AccordionV2";
import type { ComposedItem as AccordionBulletedListItem } from "@/react/components/shared/uikit/AccordionV2/BulletedList";
import { Loader } from "@/react/components/shared/uikit/Loader";
import { useContentSpecAccessDetails } from "@circle-react/hooks/accessRegistry/useContentSpecAccessDetails";
import type { AccessGroupAccessDetails } from "@circle-react/types/AccessRegistry";

interface AccessGroupContentSpecsProps {
  accessGroupId: number;
  title?: string;
}

const i18nRoot =
  "settings.access_registry.components.access_group_content_specs";

export const AccessGroupContentSpecs = ({
  title,
  accessGroupId,
}: AccessGroupContentSpecsProps) => {
  const { data: contentSpecAccessDetails, isLoading } =
    useContentSpecAccessDetails<AccessGroupAccessDetails>(
      "AccessGroup",
      accessGroupId,
    );

  if (isLoading || !contentSpecAccessDetails?.spaces) {
    return <Loader />;
  }

  const composedItems: AccordionBulletedListItem[] = Object.values(
    contentSpecAccessDetails.spaces.reduce(
      (acc, space) => {
        const groupName = space.space_group.name;
        acc[groupName] ??= { label: groupName, items: [] };
        acc[groupName].items.push(space.name);
        return acc;
      },
      {} as Record<string, AccordionBulletedListItem>,
    ),
  );

  if (composedItems.length === 0) {
    return null;
  }

  const defaultTitle = t([i18nRoot, "total_spaces"], {
    count: contentSpecAccessDetails.spaces.length,
  });

  const finalTitle = title ?? defaultTitle;

  return (
    <AccordionV2 title={finalTitle} defaultExpanded>
      <AccordionV2.BulletedList composedItems={composedItems} />
    </AccordionV2>
  );
};
